import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { useSearchParam } from "react-use";
import { ROUTE_TEMPLATES } from "../../../constants/routes";
import {
  SourceEnum,
  TemplateStatusEnum,
  TemplateStepEnum,
} from "../../../enums/template";
import useSelectedAccount from "../../../hooks/useSelectedAccount";
import { useTokensFinished } from "../../../hooks/useTokensFinished";
import { Template } from "../../../models";
import { useAppDispatch } from "../../../redux/hooks";
import {
  useCreateTemplateMutation,
  useGetTemplateQuery,
  useUpdateTemplateMutation,
} from "../../../redux/services/templates";
import { useGetUserQuery } from "../../../redux/services/user";
import { setUpgradeModal } from "../../../redux/slices/userSlice";
import { generateRequestPayload } from "../utils/apiPayloadUtils";
import {
  validateCustomText,
  validateMediaLink,
  validateRSSFeeds,
  validateRequiredField,
} from "../utils/formValidationUtils";

export const useTemplateForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isTokensFinished } = useTokensFinished();

  let { id: currentTemplateId } = useParams();
  const redirectStepParam = useSearchParam("redirect_to_step");

  const [currentStep, setCurrentStep] = useState(TemplateStepEnum.SETUP);
  const [template, setTemplate] = useState<Template | null>(null);

  const { accountId, timezone } = useSelectedAccount();

  const { data: userData } = useGetUserQuery(); // user api

  const { data: templateAPIResponse } = useGetTemplateQuery(
    {
      accountId: accountId,
      templateId: currentTemplateId,
    },
    { skip: !accountId || !currentTemplateId }
  ); // template api

  const [
    createTemplate,
    {
      isLoading: isCreatingTemplate,
      isSuccess: isCreateSuccess,
      isError: isCreateError,
      error: createError,
    },
  ] = useCreateTemplateMutation(); // create template api

  const [
    updateTemplateById,
    {
      isLoading: isUpdatingTemplate,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateTemplateMutation(); // update template api

  useEffect(() => {
    // redirecting from destination screen
    if (
      redirectStepParam &&
      redirectStepParam === TemplateStepEnum.DESTINATION
    ) {
      setCurrentStep(TemplateStepEnum.DESTINATION);
    }
  }, [redirectStepParam]);

  // useEffect(() => {
  //   // fetch template data on edit
  //   if (currentTemplateId) {
  //     getTemplate();
  //   }
  // }, [currentTemplateId, getTemplate]);

  useEffect(() => {
    // initialize template form
    if (!accountId) return;

    if (templateAPIResponse) {
      const {
        destinationId,
        destinationInfoList,
        sourceId,
        mediaUrl,
        rssFeedUrls,
      } = templateAPIResponse;
      let formValues = {
        ...templateAPIResponse,
        accountId,
        destinationId: destinationId || destinationInfoList?.[0]?.destinationId,
        rssFeedUrls: rssFeedUrls?.replaceAll(", ", "\n"),
      };

      if (sourceId === SourceEnum.UPLOAD_MEDIA) {
        formValues = {
          ...formValues,
          uploadMediaUrl: mediaUrl,
          mediaUrl: null,
        };
      }
      setTemplate(new Template(formValues));
    } else {
      const initialFormValues = new Template({
        sourceId: SourceEnum.INSTAGRAM_REELS,
        includeWebEmbed: true,
        includeIntro: true,
        accountId: accountId,
        bio: userData?.bio || `${userData?.firstName} ${userData?.lastName}`,
        timezone: timezone,
      });
      setTemplate(initialFormValues);
    }
  }, [templateAPIResponse, userData, timezone, accountId]);

  useEffect(() => {
    // on template create / edit, success / error
    if (isCreateSuccess || isUpdateSuccess) {
      toast.success(
        `Successfully ${isCreateSuccess ? "created" : "updated"} template.`
      );
      navigate(ROUTE_TEMPLATES);
    } else if (isCreateError || isUpdateError || createError || updateError) {
      toast.error(
        ((createError || updateError) as any)?.data?.detail ||
        "Something went wrong! Please try again."
      );
    }
  }, [
    isCreateSuccess,
    isUpdateSuccess,
    isCreateError,
    isUpdateError,
    createError,
    updateError,
    navigate,
  ]);

  const handleValidation = (values: Template) => {
    const errors: any = {};

    errors.name = validateRequiredField(values.name, "Name");
    if (
      values.isAutoEnabled &&
      [
        SourceEnum.INSTAGRAM_REELS,
        SourceEnum.YOUTUBE_SHORTS,
        SourceEnum.YOUTUBE_VIDEOS,
        SourceEnum.APPLE_PODCASTS,
      ].includes(values?.sourceId)
    ) {
      errors.username = validateRequiredField(values.username, "Username");
    }

    switch (values.sourceId) {
      case SourceEnum.UPLOAD_MEDIA:
        errors.uploadMediaUrl = validateRequiredField(
          values.uploadMediaUrl,
          "Audio or video"
        );
        break;
      case SourceEnum.RSS_FEEDS:
        errors.rssFeedUrls = validateRSSFeeds(values.rssFeedUrls);
        break;
      case SourceEnum.TEXT_BLOCK:
        errors.textBlock = validateCustomText(values.textBlock);
        break;
      case SourceEnum.INSTAGRAM_REELS:
      case SourceEnum.YOUTUBE_VIDEOS:
      case SourceEnum.YOUTUBE_SHORTS:
      case SourceEnum.APPLE_PODCASTS:
        if (!values.isAutoEnabled) {
          errors.mediaUrl = validateMediaLink(values.mediaUrl, values.sourceId);
        }
        break;
      case SourceEnum.TOP_ITEMS:
        errors.topItemsTopic = validateRequiredField(
          values.topItemsTopic,
          "Top Items Topic"
        );
        break;
    }

    return Object.fromEntries(
      Object.entries(errors).filter(([_, v]) => v !== undefined)
    );
  };

  const handleSubmit = async (values: Template) => {
    if (values?.templateStatusId === TemplateStatusEnum.QUEUED) return;

    if (isTokensFinished) {
      dispatch(setUpgradeModal(true));
      return;
    }

    const requestPayload = generateRequestPayload(values);

    try {
      if (currentTemplateId) {
        await updateTemplateById({
          ...requestPayload,
          accountId,
        })
          .unwrap()
          .catch(() => { });
      } else {
        await createTemplate({
          ...requestPayload,
          accountId,
        })
          .unwrap()
          .catch(() => { });
      }
    } catch (error) {
      // Error handling is already done in useEffect
    }
  };

  return {
    // State
    currentStep,
    setCurrentStep,
    template,

    // Form handlers
    handleValidation,
    handleSubmit,

    // Status flags
    isLoading: isCreatingTemplate || isUpdatingTemplate,
    isCreate: !currentTemplateId,
  };
};
